
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/profiles';
import { useToast } from '@/hooks/use-toast';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

export const useProfile = (userId?: string) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchProfile(userId);
    }
  }, [userId]);

  const fetchProfile = async (id: string) => {
    try {
      setLoading(true);
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { data, error } = await supabase.rpc('pin_get_profile', {
          p_pin: pin,
          p_profile_id: id,
        });
        if (error) throw error;
        const rows = (data as any[]) || [];
        const p = rows[0];
        if (!p) throw new Error('Profil introuvable');
        setProfile(p as Profile);
        return;
      }

      const { data, error } = await supabase.from('profiles').select('*').eq('id', id).single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le profil",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!profile) return;

    try {
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_profile', {
          p_pin: pin,
          p_profile_id: profile.id,
          p_first_name: updates.first_name ?? null,
          p_last_name: updates.last_name ?? null,
          p_email: updates.email ?? null,
          p_phone: updates.phone ?? null,
          p_position: updates.position ?? null,
          p_hourly_rate: updates.hourly_rate ?? null,
          p_hire_date: updates.hire_date ?? null,
          p_is_active: typeof updates.is_active === 'boolean' ? updates.is_active : null,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('profiles').update(updates).eq('id', profile.id);
        if (error) throw error;
      }

      setProfile({ ...profile, ...updates });
      toast({
        title: "Succès",
        description: "Profil mis à jour avec succès",
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le profil",
        variant: "destructive",
      });
    }
  };

  return { profile, loading, updateProfile, refetch: () => fetchProfile(profile?.id || '') };
};
